import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Inbox, Map as MapIcon, Repeat,
  CheckCircle2, Clock, AlertTriangle, Circle,
} from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import EmptyState from './EmptyState';
import { HitosOverlay } from './gantt/HitosLane';
import { addDays, diffDays, xDias, anchoDias } from './gantt/constants';
import useCamaraGantt from './gantt/useCamaraGantt';
import usePaneo from './gantt/usePaneo';
import ControlZoom from './gantt/ControlZoom';
import { agruparCapas, OPACIDAD_CAPA } from './gantt/GanttHeader';

// ---------- Vista Roadmap global (proyectos en el tiempo, vista estrategica) ----------
//
// Diferente al Gantt en concepto:
//   - Gantt = operativo. Tareas como filas, zoom de dias/semanas, drag para mover.
//   - Roadmap = estrategico. Proyectos como filas, zoom de trimestres/meses,
//     bandas Q1-Q4 de fondo, agrupados por estado, health chip, read-only.
//
// Pensado para reuniones de status y planeacion trimestral, no para mover fechas.
// Toda interaccion mas alla del click navega al proyecto (donde si se puede editar).

const ZOOM = {
  mes:        { px: 14, label: 'Mes' },
  trimestre:  { px: 5,  label: 'Trimestre' },
  ano:        { px: 1.8, label: 'Año' },
};
// De menos zoom a más zoom (el orden de la lupa).
const ZOOM_ORDEN = ['ano', 'trimestre', 'mes'];

const DAYS_BEFORE = 180;
const DAYS_AFTER = 540;
const ROW_HEIGHT = 56;
const SIDEBAR_WIDTH = 300;
const BAR_HEIGHT = 24;

const MES_CORTO = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

// Colores muy sutiles para bandas Q1-Q4 — apenas un wash.
const QUARTER_BG = ['rgba(6,182,212,0.04)', 'rgba(139,92,246,0.04)', 'rgba(16,185,129,0.04)', 'rgba(245,158,11,0.04)'];

// ---------- Helpers ----------

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const startOfQuarter = (d) => new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
const startOfYear = (d) => new Date(d.getFullYear(), 0, 1);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const quarterOf = (d) => Math.floor(d.getMonth() / 3) + 1;

const formatFechaCorta = (d) => {
  if (!d) return '';
  return `${MES_CORTO[d.getMonth()]} ${d.getFullYear().toString().slice(2)}`;
};

// Clasificacion del proyecto en uno de los buckets del roadmap.
// Decide a que grupo pertenece y devuelve la metadata visual del grupo.
const clasificarProyecto = (p) => {
  if (p.es_continuo) return 'continuo';
  if (!p.rangoMin) return 'sin_programar';
  const total = p.totalTareas;
  const hechas = p.tareasHechas;
  if (total > 0 && hechas === total) return 'completado';
  if (p.tieneEnCurso) return 'en_curso';
  return 'por_iniciar';
};

const GRUPOS = [
  { key: 'en_curso',      label: 'En curso',      color: '#06b6d4', icon: Clock },
  { key: 'por_iniciar',   label: 'Por iniciar',   color: '#64748b', icon: Circle },
  { key: 'continuo',      label: 'Continuos',     color: '#8b5cf6', icon: Repeat },
  { key: 'completado',    label: 'Completados',   color: '#10b981', icon: CheckCircle2 },
  { key: 'sin_programar', label: 'Sin programar', color: '#94a3b8', icon: Inbox },
];

// Health chip: a tiempo / en riesgo / atrasado / completado / continuo / sin.
// Calculo de "esperado": linealmente entre rangoMin y rangoMax.
const calcularHealth = (p, today) => {
  if (p.es_continuo) return { key: 'continuo', label: 'Continuo', color: 'violet' };
  if (!p.rangoMin || !p.rangoMax) return null;
  if (p.totalTareas > 0 && p.tareasHechas === p.totalTareas) {
    return { key: 'completado', label: 'Completado', color: 'emerald' };
  }
  if (p.rangoMax < today) {
    return { key: 'atrasado', label: 'Atrasado', color: 'rose' };
  }
  const totalDias = Math.max(1, diffDays(p.rangoMax, p.rangoMin) + 1);
  const transcurridos = Math.max(0, Math.min(totalDias, diffDays(today, p.rangoMin)));
  const esperadoPct = (transcurridos / totalDias) * 100;
  const realPct = p.progresoPct;
  if (realPct < esperadoPct - 15) {
    return { key: 'en_riesgo', label: 'En riesgo', color: 'amber' };
  }
  return { key: 'a_tiempo', label: 'A tiempo', color: 'cyan' };
};

const HEALTH_STYLES = {
  cyan:    'bg-cyan-50 text-cyan-700 border-cyan-200/70',
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200/70',
  amber:   'bg-amber-50 text-amber-700 border-amber-200/70',
  rose:    'bg-rose-50 text-rose-700 border-rose-200/70',
  violet:  'bg-violet-50 text-violet-700 border-violet-200/70',
};

// ---------- Componente principal ----------

const RoadmapGlobalView = () => {
  const navigate = useNavigate();
  const [proyectos, setProyectos] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const scrollRef = useRef(null);
  // Lienzo de la linea de tiempo: la camara le escribe --dw (ancho del dia).
  const lienzoRef = useRef(null);

  const { firstDay, totalDays, todayOffset, today } = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return {
      firstDay: addDays(t, -DAYS_BEFORE),
      totalDays: DAYS_BEFORE + DAYS_AFTER,
      todayOffset: DAYS_BEFORE,
      today: t,
    };
  }, []);

  // Zoom con la misma camara del Gantt (ver useCamaraGantt). La ventana del
  // Roadmap es fija, igual para todas las escalas; hoy queda a un tercio del
  // ancho para que se vea mas futuro que pasado.
  const ventanaPara = useCallback(
    () => ({ firstDay, totalDays, todayOffset }),
    [firstDay, totalDays, todayOffset]
  );
  const camara = useCamaraGantt({
    zoomInicial: 'trimestre',
    ventanaPara,
    scrollRef,
    lienzoRef,
    escalas: ZOOM,
    hoyEn: 1 / 3,
  });
  const { zoom, dayWidthTextos } = camara;

  // La mano: arrastrar el fondo recorre la linea de tiempo.
  const paneo = usePaneo(scrollRef);

  // ----- Carga -----

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [proyectosResp, tareasResp] = await Promise.all([
        axiosInstance.get('/api/proyectos'),
        axiosInstance.get('/api/tareas'),
      ]);
      setProyectos(proyectosResp.data || []);
      setTareas(tareasResp.data || []);
    } catch (err) {
      console.error('Error cargando roadmap:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ----- Derivar metadatos por proyecto + agrupar -----

  const proyectosPorGrupo = useMemo(() => {
    const tareasPorProyecto = new Map();
    tareas.forEach((t) => {
      if (!tareasPorProyecto.has(t.proyecto_id)) tareasPorProyecto.set(t.proyecto_id, []);
      tareasPorProyecto.get(t.proyecto_id).push(t);
    });

    const enriquecidos = proyectos
      .filter((p) => !p.archivado)
      .map((p) => {
        const ts = tareasPorProyecto.get(p.id) || [];
        let tieneEnCurso = false;
        // Hitos unicos del proyecto (dedup por id) derivados de las tareas: el
        // listado global no trae proyecto.hitos, pero cada tarea con hito lo
        // trae embebido en tarea.hito.
        const hitosMap = new Map();
        ts.forEach((t) => {
          if (t.estado?.categoria === 'en_curso') tieneEnCurso = true;
          if (t.hito && t.hito.id != null && t.hito.fecha && !hitosMap.has(t.hito.id)) {
            hitosMap.set(t.hito.id, t.hito);
          }
        });
        // Las fechas del proyecto son PROPIAS — no se derivan de las tareas.
        // El usuario puede ajustarlas en el Roadmap (drag/resize) o en config.
        const min = p.fecha_inicio ? new Date(p.fecha_inicio + 'T00:00:00') : null;
        const max = p.fecha_fin ? new Date(p.fecha_fin + 'T00:00:00') : null;
        const total = p.stats?.total ?? ts.length;
        const hechas = p.stats?.hechas ?? 0;
        const pct = total > 0 ? Math.round((hechas / total) * 100) : 0;
        return {
          ...p,
          rangoMin: min,
          rangoMax: max,
          totalTareas: total,
          tareasHechas: hechas,
          tareasActivas: p.stats?.activas ?? Math.max(0, total - hechas),
          progresoPct: pct,
          tieneEnCurso,
          hitos: Array.from(hitosMap.values()),
        };
      });

    // Agrupar
    const buckets = Object.fromEntries(GRUPOS.map((g) => [g.key, []]));
    enriquecidos.forEach((p) => {
      const k = clasificarProyecto(p);
      buckets[k].push(p);
    });

    // Orden dentro del grupo: programados por fecha de inicio asc, sin programar alfabetico.
    Object.keys(buckets).forEach((k) => {
      buckets[k].sort((a, b) => {
        if (a.rangoMin && b.rangoMin) return a.rangoMin.getTime() - b.rangoMin.getTime();
        if (a.rangoMin) return -1;
        if (b.rangoMin) return 1;
        return a.nombre.localeCompare(b.nombre);
      });
    });

    return GRUPOS
      .map((g) => ({ ...g, proyectos: buckets[g.key] }))
      .filter((g) => g.proyectos.length > 0);
  }, [proyectos, tareas]);

  const totalProyectos = useMemo(
    () => proyectosPorGrupo.reduce((s, g) => s + g.proyectos.length, 0),
    [proyectosPorGrupo]
  );

  // ----- Acciones -----

  const handleProyectoClick = useCallback((slug) => {
    navigate(`/proyectos/${slug}`);
  }, [navigate]);

  // Update optimista de fechas del proyecto (drag/resize en la barra). patch =
  // { fecha_inicio?, fecha_fin? } como strings ISO YYYY-MM-DD. Si el PUT falla,
  // refetchamos para revertir al estado del servidor.
  const handleUpdateFechas = useCallback(async (proyectoId, patch) => {
    setProyectos((prev) => prev.map((p) => p.id === proyectoId ? { ...p, ...patch } : p));
    try {
      await axiosInstance.put(`/api/proyectos/${proyectoId}`, patch);
    } catch (err) {
      console.error('Error actualizando fechas del proyecto:', err);
      fetchAll();
    }
  }, [fetchAll]);

  const toggleGroup = useCallback((key) => {
    setCollapsedGroups((c) => ({ ...c, [key]: !c[key] }));
  }, []);

  // ----- Bandas de trimestre (background) -----

  const bandasQuarter = useMemo(() => {
    const bandas = [];
    let cur = startOfQuarter(firstDay);
    while (diffDays(cur, firstDay) < totalDays) {
      const next = addMonths(cur, 3);
      const offsetDias = Math.max(0, diffDays(cur, firstDay));
      const endOffset = Math.min(totalDays, diffDays(next, firstDay));
      const widthDias = endOffset - offsetDias;
      if (widthDias > 0) {
        bandas.push({
          key: `${cur.getFullYear()}-Q${quarterOf(cur)}`,
          offsetDias,
          widthDias,
          bg: QUARTER_BG[(quarterOf(cur) - 1) % 4],
        });
      }
      cur = next;
    }
    return bandas;
  }, [firstDay, totalDays]);

  return (
    <div className="relative h-full flex flex-col bg-white">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-6 pt-4 pb-3 border-b border-slate-200/60 flex-shrink-0">
        <div className="flex items-center gap-2">
          <button
            onClick={camara.irAHoy}
            className="px-2 py-1 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
          >
            Hoy
          </button>
          <div className="h-4 w-px bg-slate-200 mx-1" />
          <ControlZoom zoom={zoom} orden={ZOOM_ORDEN} escalas={ZOOM} onCambiar={camara.cambiarZoom} />
        </div>
        <span className="text-xs text-slate-400 tabular-nums">
          {totalProyectos} {totalProyectos === 1 ? 'proyecto' : 'proyectos'}
        </span>
      </div>

      {/* Body */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Sidebar */}
        <div className="flex flex-col flex-shrink-0 border-r border-slate-200/60 bg-white" style={{ width: SIDEBAR_WIDTH }}>
          <div className="border-b border-slate-200/60 bg-slate-50/40" style={{ height: 64 }}>
            <div className="flex items-center h-full px-4">
              <span className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">
                Proyecto
              </span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto" id="roadmap-sidebar-scroll">
            {proyectosPorGrupo.map((g) => {
              const Icon = g.icon;
              const isCol = collapsedGroups[g.key];
              return (
                <div key={g.key}>
                  <button
                    onClick={() => toggleGroup(g.key)}
                    className="w-full flex items-center gap-2 px-4 bg-slate-50/60 hover:bg-slate-100/70 border-b border-slate-200/50 transition-colors group"
                    style={{ height: 32 }}
                  >
                    <motion.span
                      animate={{ rotate: isCol ? -90 : 0 }}
                      transition={{ duration: 0.15 }}
                      className="text-slate-400 group-hover:text-slate-600"
                    >
                      <Icon size={11} strokeWidth={2.2} style={{ color: g.color }} />
                    </motion.span>
                    <span className="text-[11px] font-bold tracking-[0.1em] uppercase text-slate-600">
                      {g.label}
                    </span>
                    <span className="text-[10px] font-medium text-slate-400 tabular-nums ml-auto">
                      {g.proyectos.length}
                    </span>
                  </button>

                  {!isCol && g.proyectos.map((p) => {
                    const health = calcularHealth(p, today);
                    return (
                      <button
                        key={p.id}
                        onClick={() => handleProyectoClick(p.slug)}
                        className="w-full flex items-center gap-2.5 px-4 text-left hover:bg-slate-50 transition-colors border-b border-slate-100"
                        style={{ height: ROW_HEIGHT }}
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                          style={{ backgroundColor: p.color }}
                        />
                        <span className="flex-1 min-w-0">
                          <span className="block text-[12.5px] font-semibold tracking-tight text-slate-800 truncate">
                            {p.nombre}
                          </span>
                          <span className="flex items-center gap-1.5 mt-0.5">
                            {health && (
                              <span
                                className={`inline-flex items-center px-1.5 py-0 text-[9px] font-medium tracking-wide uppercase rounded border ${HEALTH_STYLES[health.color]}`}
                              >
                                {health.label}
                              </span>
                            )}
                            <span className="text-[10px] text-slate-400 tabular-nums truncate">
                              {p.es_continuo
                                ? `${p.tareasActivas} activas`
                                : p.rangoMin
                                  ? `${p.progresoPct}% · ${p.tareasHechas}/${p.totalTareas}`
                                  : `${p.totalTareas} tareas sin fecha`}
                            </span>
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })}

            {!loading && proyectosPorGrupo.length === 0 && (
              <EmptyState icon={Inbox} title="Sin proyectos para mostrar." variant="card" />
            )}
          </div>
        </div>

        {/* Roadmap canvas */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-auto cursor-grab"
          onMouseDown={paneo.onMouseDown}
          onWheel={paneo.onWheel}
          onScroll={(e) => {
            camara.registrarScroll(e.currentTarget);
            const sidebar = document.getElementById('roadmap-sidebar-scroll');
            if (sidebar && Math.abs(sidebar.scrollTop - e.target.scrollTop) > 1) {
              sidebar.scrollTop = e.target.scrollTop;
            }
          }}
        >
          <div ref={lienzoRef} className="relative" style={{ width: anchoDias(totalDays) }}>
            <RoadmapHeader
              firstDay={firstDay}
              totalDays={totalDays}
              capas={camara.capas}
            />

            <div className="relative">
              {/* Bandas Q1-Q4 de fondo */}
              <div className="absolute inset-0 pointer-events-none">
                {bandasQuarter.map((b) => (
                  <div
                    key={b.key}
                    className="absolute top-0 bottom-0"
                    style={{ left: xDias(b.offsetDias), width: anchoDias(b.widthDias), backgroundColor: b.bg }}
                  />
                ))}
              </div>

              {/* Linea Hoy */}
              <div
                className="absolute top-0 bottom-0 w-px bg-rose-500/70 pointer-events-none z-10"
                style={{ left: xDias(todayOffset) }}
              />
              <div
                className="absolute top-0 pointer-events-none z-10"
                style={{ left: anchoDias(todayOffset, 16) }}
              >
                <div className="px-1.5 py-0.5 bg-rose-500 text-white text-[9px] font-bold tracking-wide rounded-sm">
                  HOY
                </div>
              </div>

              {/* Filas por grupo */}
              {proyectosPorGrupo.map((g) => {
                const isCol = collapsedGroups[g.key];
                return (
                  <div key={g.key}>
                    {/* Header de grupo (sincronizado con sidebar) */}
                    <div
                      className="relative border-b border-slate-200/50 bg-slate-50/30"
                      style={{ height: 32 }}
                    />
                    {!isCol && g.proyectos.map((p) => (
                      <div
                        key={p.id}
                        className="relative border-b border-slate-100 hover:bg-slate-50/40"
                        style={{ height: ROW_HEIGHT }}
                      >
                        <ProjectBar
                          proyecto={p}
                          firstDay={firstDay}
                          dayWidth={dayWidthTextos}
                          totalDays={totalDays}
                          today={today}
                          onClick={() => handleProyectoClick(p.slug)}
                          onUpdateFechas={handleUpdateFechas}
                        />
                        {/* Hitos del proyecto: rombos pequenos en la x de su fecha,
                            sobre el borde inferior de la barra para no tapar el
                            % ni el rango que van dentro. */}
                        <HitosOverlay
                          hitos={p.hitos}
                          firstDay={firstDay}
                          cy={ROW_HEIGHT / 2 + BAR_HEIGHT / 2}
                          size={9}
                          enLienzo
                        />
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Overlay loading */}
      {loading && (
        <div className="absolute inset-x-0 bottom-0 top-[105px] flex pointer-events-none">
          <div className="flex-shrink-0" style={{ width: SIDEBAR_WIDTH }} />
          <div className="flex-1 overflow-hidden p-3 space-y-3">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="h-6 bg-slate-100 rounded-full skeleton"
                style={{ width: `${35 + (i * 17) % 55}%`, marginLeft: `${(i * 11) % 35}%` }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && proyectosPorGrupo.length === 0 && (
        <div className="absolute inset-x-0 bottom-0 top-[105px] pointer-events-none">
          <EmptyState
            icon={MapIcon}
            title="Sin proyectos en el roadmap"
            description="Cuando tengas proyectos activos van a aparecer acá agrupados por estado."
            className="pointer-events-auto"
          />
        </div>
      )}
    </div>
  );
};

// ---------- Header del Roadmap ----------
//
// Tres niveles segun zoom:
//   - ano:       fila 1 = años, fila 2 = trimestres (Q1/Q2/Q3/Q4)
//   - trimestre: fila 1 = trimestres, fila 2 = meses
//   - mes:       fila 1 = trimestres, fila 2 = meses (mas detalle, mas px/dia)
//
// Se ubica con --dw (sigue a la camara). Mientras la camara viaja entre Año y
// Trimestre, las filas que cambian se funden; Trimestre y Mes comparten filas,
// asi que esas quedan fijas.

const tramos = (firstDay, totalDays, inicio, siguiente, armar) => {
  const items = [];
  let cur = inicio(firstDay);
  while (diffDays(cur, firstDay) < totalDays) {
    const next = siguiente(cur);
    const offsetDias = Math.max(0, diffDays(cur, firstDay));
    const endOffset = Math.min(totalDays, diffDays(next, firstDay));
    items.push({ ...armar(cur), offsetDias, widthDias: endOffset - offsetDias });
    cur = next;
  }
  return items;
};

const RoadmapHeader = ({ firstDay, totalDays, capas }) => {
  const unidades = useMemo(() => ({
    anios: tramos(firstDay, totalDays, startOfYear, (d) => new Date(d.getFullYear() + 1, 0, 1), (d) => ({
      key: `y-${d.getFullYear()}`, label: d.getFullYear().toString(),
    })),
    trimestresArriba: tramos(firstDay, totalDays, startOfQuarter, (d) => addMonths(d, 3), (d) => ({
      key: `q-${d.getFullYear()}-${quarterOf(d)}`, label: `Q${quarterOf(d)} ${d.getFullYear()}`,
    })),
    trimestresAbajo: tramos(firstDay, totalDays, startOfQuarter, (d) => addMonths(d, 3), (d) => ({
      key: `q-${d.getFullYear()}-${quarterOf(d)}`, label: `Q${quarterOf(d)}`,
    })),
    meses: tramos(firstDay, totalDays, startOfMonth, (d) => addMonths(d, 1), (d) => ({
      key: `m-${d.getFullYear()}-${d.getMonth()}`, mes: d.getMonth(), anio: d.getFullYear(),
    })),
  }), [firstDay, totalDays]);

  const superior = agruparCapas(capas, (c) => (c.zoom === 'ano'
    ? { tipo: 'anios', unidades: unidades.anios }
    : { tipo: 'trimestres', unidades: unidades.trimestresArriba }));
  const inferior = agruparCapas(capas, (c) => (c.zoom === 'ano'
    ? { tipo: 'trimestres', unidades: unidades.trimestresAbajo }
    : { tipo: 'meses', unidades: unidades.meses }));

  // Etiqueta de un mes segun cuanto mide en la escala de su capa.
  const etiquetaMes = (s, zoom) => {
    const widthPx = s.widthDias * ZOOM[zoom].px;
    if (widthPx >= 90) return `${MES_CORTO[s.mes]} ${s.anio}`;
    return widthPx >= 40 ? MES_CORTO[s.mes] : '';
  };

  return (
    <div className="sticky top-0 z-20 bg-white border-b border-slate-200/60">
      {/* Fila superior (año o trimestre) */}
      <div className="relative h-8 border-b border-slate-200/40 bg-slate-50/60">
        {superior.map((f) => (
          <div key={f.tipo} className="absolute inset-0" style={{ opacity: OPACIDAD_CAPA[f.rol] }}>
            {f.unidades.map((s) => (
              <div
                key={s.key}
                className="absolute top-0 bottom-0 flex items-center px-2 text-[11px] font-bold tracking-wide text-slate-700 border-r border-slate-200/60 whitespace-nowrap overflow-hidden"
                style={{ left: xDias(s.offsetDias), width: anchoDias(s.widthDias) }}
              >
                {s.label}
              </div>
            ))}
          </div>
        ))}
      </div>
      {/* Fila inferior (trimestre o mes) */}
      <div className="relative h-8">
        {inferior.map((f) => (
          <div key={f.tipo} className="absolute inset-0" style={{ opacity: OPACIDAD_CAPA[f.rol] }}>
            {f.unidades.map((s) => (
              <div
                key={s.key}
                className="absolute top-0 bottom-0 flex items-center justify-center text-[10px] font-medium text-slate-500 border-r border-slate-200/40 whitespace-nowrap overflow-hidden"
                style={{ left: xDias(s.offsetDias), width: anchoDias(s.widthDias) }}
              >
                {f.tipo === 'meses' ? etiquetaMes(s, f.zoom) : s.label}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Barra de proyecto (drag + resize) ----------
//
// Una sola barra por proyecto. Interacciones:
//   - mousedown en el cuerpo  -> drag para mover el rango entero
//   - mousedown en handle izq -> resize de fecha_inicio
//   - mousedown en handle der -> resize de fecha_fin (no aplica a continuos)
//   - click simple (sin drag) -> navega al proyecto
//
// Update optimista: la barra se mueve al instante via state local de preview,
// y al soltar se llama onUpdateFechas que dispara el PUT.

const DRAG_THRESHOLD_PX = 4;  // delta minimo para considerar drag (no click)

const toISO = (d) => {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const ProjectBar = ({ proyecto, firstDay, dayWidth, totalDays, today, onClick, onUpdateFechas }) => {
  const [dragMode, setDragMode] = useState(null); // 'move' | 'resize-left' | 'resize-right' | null
  const [preview, setPreview] = useState(null);   // { inicio: Date, fin: Date } durante drag
  const dragRef = useRef({});

  const inicio = preview?.inicio || proyecto.rangoMin;
  const finOriginal = preview?.fin || proyecto.rangoMax;
  // Continuo: barra abierta hasta el final del viewport con fade.
  const finReal = proyecto.es_continuo
    ? addDays(firstDay, totalDays)
    : finOriginal;

  // Listener global de mouse: tomamos sobre window mientras dragMode != null.
  useEffect(() => {
    if (!dragMode) return;

    const onMove = (e) => {
      const r = dragRef.current;
      const deltaPx = e.clientX - r.startX;
      const deltaDias = Math.round(deltaPx / dayWidth);
      if (!r.isDragging && Math.abs(deltaPx) >= DRAG_THRESHOLD_PX) r.isDragging = true;
      if (!r.isDragging) return;

      if (dragMode === 'move') {
        setPreview({
          inicio: addDays(r.startInicio, deltaDias),
          fin: r.startFin ? addDays(r.startFin, deltaDias) : null,
        });
      } else if (dragMode === 'resize-left') {
        // No dejar pasar el inicio mas alla del fin
        let nuevoInicio = addDays(r.startInicio, deltaDias);
        if (r.startFin && nuevoInicio > r.startFin) nuevoInicio = r.startFin;
        setPreview({ inicio: nuevoInicio, fin: r.startFin });
      } else if (dragMode === 'resize-right') {
        let nuevoFin = addDays(r.startFin, deltaDias);
        if (nuevoFin < r.startInicio) nuevoFin = r.startInicio;
        setPreview({ inicio: r.startInicio, fin: nuevoFin });
      }
    };

    const onUp = () => {
      const r = dragRef.current;
      const wasDragging = r.isDragging;
      setDragMode(null);
      if (wasDragging && preview) {
        // Persistir
        const patch = {};
        if (preview.inicio && (!proyecto.rangoMin || preview.inicio.getTime() !== proyecto.rangoMin.getTime())) {
          patch.fecha_inicio = toISO(preview.inicio);
        }
        if (preview.fin && (!proyecto.rangoMax || preview.fin.getTime() !== proyecto.rangoMax.getTime())) {
          patch.fecha_fin = toISO(preview.fin);
        }
        if (Object.keys(patch).length > 0) {
          onUpdateFechas?.(proyecto.id, patch);
        }
      } else if (!wasDragging) {
        // Click sin drag -> navegar (replicamos el comportamiento del boton)
        onClick?.();
      }
      setPreview(null);
      dragRef.current = {};
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragMode, preview, dayWidth, proyecto.id, proyecto.rangoMin, proyecto.rangoMax, onUpdateFechas, onClick]);

  const startDrag = (e, modo) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = {
      startX: e.clientX,
      startInicio: proyecto.rangoMin || new Date(),
      startFin: proyecto.rangoMax,
      isDragging: false,
    };
    setDragMode(modo);
  };

  if (!inicio || !finReal) {
    // Proyecto sin programar — no dibujamos barra (el sidebar ya lista).
    return null;
  }

  const offsetDays = diffDays(inicio, firstDay);
  const lastIndex = diffDays(finReal, firstDay) + 1;
  const durationDays = Math.max(1, lastIndex - offsetDays);

  // Posicion y ancho con --dw (siguen a la camara); el ancho en px solo decide
  // que textos y marcas entran en la barra.
  const width = durationDays * dayWidth;
  const top = (ROW_HEIGHT - BAR_HEIGHT) / 2;

  const color = proyecto.color || '#06b6d4';
  const pct = proyecto.es_continuo ? null : Math.min(100, Math.max(0, proyecto.progresoPct));
  const completado = pct === 100;
  const finParaVencido = preview?.fin || proyecto.rangoMax;
  const vencido = !proyecto.es_continuo
    && !completado
    && finParaVencido
    && finParaVencido < today;

  // Linea de "esperado" (donde deberia estar el % segun fecha)
  let esperadoFraccion = null;
  const inicioParaEsperado = preview?.inicio || proyecto.rangoMin;
  if (!proyecto.es_continuo && !completado && finParaVencido && inicioParaEsperado) {
    const totalDias = Math.max(1, diffDays(finParaVencido, inicioParaEsperado) + 1);
    const transcurridos = Math.max(0, Math.min(totalDias, diffDays(today, inicioParaEsperado)));
    esperadoFraccion = transcurridos / totalDias;
  }
  const esperadoX = esperadoFraccion != null ? esperadoFraccion * width : null;

  const formatRango = () => {
    if (proyecto.es_continuo) return `Desde ${formatFechaCorta(inicioParaEsperado)}`;
    return `${formatFechaCorta(inicioParaEsperado)} – ${formatFechaCorta(finParaVencido)}`;
  };

  const labelInterno = proyecto.es_continuo
    ? proyecto.nombre
    : (pct != null ? `${pct}%` : '');
  const mostrarLabel = width >= 80 && dayWidth >= 4;

  const cursorClass = dragMode === 'move'
    ? 'cursor-grabbing'
    : 'cursor-grab';

  return (
    <div
      className={`absolute group rounded-md ${cursorClass} ${dragMode ? 'z-20' : ''}`}
      style={{ left: xDias(offsetDays), width: anchoDias(durationDays), top, height: BAR_HEIGHT, userSelect: 'none' }}
      title={`${proyecto.nombre} · ${formatRango()}${pct != null ? ` · ${pct}%` : ''}`}
      onMouseDown={(e) => startDrag(e, 'move')}
    >
      {/* Track (pendiente) */}
      <div
        className="absolute inset-0 rounded-md transition-all group-hover:brightness-105 pointer-events-none"
        style={{
          backgroundColor: color,
          opacity: 0.16,
          boxShadow: vencido
            ? '0 0 0 1px rgba(244,63,94,0.55) inset'
            : `0 0 0 1px ${color}55 inset`,
        }}
      />
      {/* Fill de progreso */}
      {pct != null && pct > 0 && (
        <div
          className="absolute inset-y-0 left-0 rounded-md transition-all pointer-events-none"
          style={{
            backgroundColor: color,
            width: `${pct}%`,
            opacity: completado ? 0.85 : 0.7,
          }}
        />
      )}
      {/* Fade de continuo a la derecha */}
      {proyecto.es_continuo && (
        <div
          className="absolute inset-y-0 right-0 rounded-r-md pointer-events-none"
          style={{
            width: 'min(80px, 50%)',
            background: `linear-gradient(to right, ${color}33 0%, transparent 100%)`,
          }}
        />
      )}
      {/* Linea de "esperado" (vertical tenue) */}
      {esperadoX != null && esperadoX > 4 && esperadoX < width - 4 && (
        <div
          className="absolute top-0 bottom-0 w-px bg-slate-700/30 pointer-events-none"
          style={{ left: `${esperadoFraccion * 100}%` }}
        />
      )}
      {/* Label interno */}
      {mostrarLabel && (
        <span className="absolute inset-0 flex items-center justify-between px-2 pointer-events-none">
          <span className="text-[10px] font-semibold tracking-tight text-white truncate drop-shadow-[0_1px_1px_rgba(0,0,0,0.25)]">
            {proyecto.es_continuo
              ? <span className="inline-flex items-center gap-1"><Repeat size={10} strokeWidth={2.5} /> {labelInterno}</span>
              : labelInterno}
          </span>
          {!proyecto.es_continuo && width >= 180 && (
            <span className="text-[9px] font-medium text-white/80 tabular-nums whitespace-nowrap drop-shadow-[0_1px_1px_rgba(0,0,0,0.25)]">
              {formatRango()}
            </span>
          )}
        </span>
      )}
      {/* Handle izquierdo — resize fecha_inicio */}
      <div
        onMouseDown={(e) => startDrag(e, 'resize-left')}
        className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/30 rounded-l-md"
        style={{ zIndex: 2 }}
        title="Arrastrar para cambiar fecha de inicio"
      />
      {/* Handle derecho — solo si no es continuo */}
      {!proyecto.es_continuo && (
        <div
          onMouseDown={(e) => startDrag(e, 'resize-right')}
          className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/30 rounded-r-md"
          style={{ zIndex: 2 }}
          title="Arrastrar para cambiar fecha de fin"
        />
      )}
      {/* Marcador de inicio y fin: pequeños diamantes a los bordes */}
      {!proyecto.es_continuo && (
        <>
          <div
            className="absolute -left-1 w-2 h-2 rotate-45 border border-white pointer-events-none"
            style={{ top: BAR_HEIGHT / 2 - 4, backgroundColor: color }}
          />
          <div
            className="absolute -right-1 w-2 h-2 rotate-45 border border-white pointer-events-none"
            style={{ top: BAR_HEIGHT / 2 - 4, backgroundColor: completado ? '#10b981' : (vencido ? '#f43f5e' : color) }}
          />
        </>
      )}
      {vencido && (
        <div className="absolute -top-1 -right-1 pointer-events-none">
          <AlertTriangle size={12} className="text-rose-500 fill-white" strokeWidth={2.5} />
        </div>
      )}
    </div>
  );
};

export default RoadmapGlobalView;
